import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth.store';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Mail, Lock, CheckCircle, AlertCircle } from 'lucide-react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { getErrorMessage } from '@/lib/errors';

const passwordSchema = z.object({
  password: z.string().min(6, 'Password must be at least 6 characters'),
  confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

type PasswordFormData = z.infer<typeof passwordSchema>;

export function EmailLinkComplete() {
  const navigate = useNavigate();
  const { signInWithLink, checkEmailLink, setPassword } = useAuthStore();
  
  const [step, setStep] = useState<'verifying' | 'email' | 'password' | 'complete'>('verifying');
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<PasswordFormData>({
    resolver: zodResolver(passwordSchema),
  });

  useEffect(() => {
    // Check if this is a valid email link
    const url = window.location.href;
    if (checkEmailLink(url)) {
      // Check if email is stored in localStorage (Firebase recommends this)
      const storedEmail = window.localStorage.getItem('emailForSignIn');
      if (storedEmail) {
        setEmail(storedEmail);
        handleEmailLinkSignIn(storedEmail);
      } else {
        // Ask user for their email
        setStep('email');
      }
    } else {
      setError('Invalid or expired sign-in link');
      setTimeout(() => navigate('/login'), 3000);
    }
  }, []);

  const handleEmailLinkSignIn = async (userEmail: string) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const result = await signInWithLink(userEmail, window.location.href);
      
      // Clear stored email
      window.localStorage.removeItem('emailForSignIn');
      
      if (result.requiresPasswordSetup) {
        setStep('password');
      } else {
        setStep('complete');
        setTimeout(() => navigate('/dashboard'), 2000);
      }
    } catch (error) {
      console.error('Email link sign-in error:', error);
      setError(getErrorMessage(error, 'Failed to complete sign-in'));
      setStep('email');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      setError('Please enter your email address');
      return;
    }
    
    // Store email for future use
    window.localStorage.setItem('emailForSignIn', email);
    handleEmailLinkSignIn(email);
  };

  const handlePasswordSetup = async (data: PasswordFormData) => {
    setIsLoading(true);
    setError(null);
    
    try {
      await setPassword(data.password);
      setStep('complete');
      setTimeout(() => navigate('/dashboard'), 2000);
    } catch (error) {
      setError(getErrorMessage(error, 'Failed to set password'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <Card className="w-full max-w-md">
        {step === 'verifying' && (
          <>
            <CardHeader className="space-y-1">
              <CardTitle className="text-2xl font-bold text-center">
                Verifying Your Link
              </CardTitle>
              <CardDescription className="text-center">
                Please wait while we verify your sign-in link...
              </CardDescription>
            </CardHeader>
            <CardContent className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
            </CardContent>
          </>
        )}

        {step === 'email' && (
          <>
            <CardHeader className="space-y-1">
              <CardTitle className="text-2xl font-bold text-center">
                Complete Sign In
              </CardTitle>
              <CardDescription className="text-center">
                Please confirm your email address to continue
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleEmailSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">
                    <Mail className="inline-block w-4 h-4 mr-2" />
                    Email Address
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="admin@bevpass.com"
                    disabled={isLoading}
                    autoComplete="email"
                    required
                  />
                  <p className="text-sm text-muted-foreground">
                    Enter the email address where you received the sign-in link
                  </p>
                </div>

                {error && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <Button
                  type="submit"
                  className="w-full"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    'Continue'
                  )}
                </Button>
              </form>
            </CardContent>
          </>
        )}

        {step === 'password' && (
          <>
            <CardHeader className="space-y-1">
              <CardTitle className="text-2xl font-bold text-center">
                Set Your Password
              </CardTitle>
              <CardDescription className="text-center">
                Create a secure password for your account
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit(handlePasswordSetup)} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="password">
                    <Lock className="inline-block w-4 h-4 mr-2" />
                    New Password
                  </Label>
                  <Input
                    {...register('password')}
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    disabled={isLoading}
                    autoComplete="new-password"
                  />
                  {errors.password && (
                    <p className="text-sm text-red-600">{errors.password.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">
                    <Lock className="inline-block w-4 h-4 mr-2" />
                    Confirm Password
                  </Label>
                  <Input
                    {...register('confirmPassword')}
                    id="confirmPassword"
                    type="password"
                    placeholder="••••••••"
                    disabled={isLoading}
                    autoComplete="new-password"
                  />
                  {errors.confirmPassword && (
                    <p className="text-sm text-red-600">{errors.confirmPassword.message}</p>
                  )}
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                  <p className="text-sm text-blue-800">
                    <strong>Password Requirements:</strong>
                  </p>
                  <ul className="text-sm text-blue-700 mt-1 ml-4 list-disc">
                    <li>At least 6 characters long</li>
                    <li>Use a mix of letters, numbers, and symbols</li>
                    <li>Avoid common words or personal information</li>
                  </ul>
                </div>

                {error && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <Button
                  type="submit"
                  className="w-full"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Setting Password...
                    </>
                  ) : (
                    'Set Password & Continue'
                  )}
                </Button>
              </form>
            </CardContent>
          </>
        )}

        {step === 'complete' && (
          <>
            <CardHeader className="space-y-1">
              <CardTitle className="text-2xl font-bold text-center">
                Welcome to BevPass Admin!
              </CardTitle>
              <CardDescription className="text-center">
                Your account has been successfully set up
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col items-center py-8 space-y-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <p className="text-center text-muted-foreground">
                Redirecting you to the dashboard...
              </p>
              <Loader2 className="h-4 w-4 animate-spin text-purple-600" />
            </CardContent>
          </>
        )}
      </Card>
    </div>
  );
}

export default EmailLinkComplete;
